/**
 * useWorkflowConfig — centralised hook for reading assessment workflow config.
 *
 * Replaces ~15 files of scattered org-name string checks with a single,
 * config-driven interface.  Every page that needs to know "is this an MDT
 * org?", "is SSI always done?", "should DF use the flat model?", etc. can
 * call this hook instead of doing its own substring matching on org names.
 *
 * RESOLUTION ORDER
 * ----------------
 * 1. Patient-level `workflowConfig` (stamped at creation from the org's
 *    Firestore config).  This is the primary path for NEW patients.
 * 2. Hardcoded preset: `ORG_WORKFLOW_CONFIGS[orgName]` from the constants
 *    file.  This covers callers that only have an org name and no patient
 *    (e.g. dashboard progress pills).
 * 3. Legacy fallback: `deriveLegacyConfig(orgName)` reproduces the exact
 *    behaviour of all current hardcoded org-name checks.  This is the path
 *    for EXISTING patients who were created before the workflow config
 *    system was deployed.
 *
 * USAGE
 * -----
 *   const wf = useWorkflowConfig(patientDataModel, claims);
 *   if (wf.isMDTReview) { ... }
 *   if (wf.isSSIFirst) { ... }
 *
 * The hook is intentionally lightweight — it does no Firestore reads and
 * has no side effects.  All data comes from props that are already loaded.
 */

import { useMemo } from 'react';
import {
  DEFAULT_WORKFLOW_CONFIG,
  DEFAULT_SUPPLEMENTARY_DOCUMENTS,
  DEFAULT_APPOINTMENT_DATES,
  DF_PREFERENCES,
  SSI_ACCESS_POLICIES,
  DF_MODELS,
  DD_STYLES,
  QB_CHECK_MODES,
  ASSESSMENT_WORKFLOW_TYPES,
  SSI_END_PAGES,
  SSI_LEAD_OVERRIDE_STATES,
  SSI_DOMAINS_IMPACTED_STATES,
  SSI_EMERGING_DIFFERENTIALS_STATES,
  SSI_APPOINTMENT_FINISH_PATH_STATES,
  CI_END_PAGES,
  CI_RISK_ASSESSMENT_STATES,
  CI_END_ASSESSMENT_DECISION_STATES,
  CI_END_DECISIONS,
  OBS_OBSERVATION_STATES,
  OBS_VERSIONS,
  DD_STRUCTURES,
  DD_DIFFERENTIALS_DECISIONS_STATES,
  PAD_MEDICATION_PLAN_STATES,
  PAD_STAGE_ORDERS,
  PAD_STRUCTURES,
  CYP_SSI_END_PAGES,
  CYP_SSI_EMERGING_DIFFERENTIALS_STATES,
  CYP_DD_STRUCTURES,
  CYP_DD_DIFFERENTIALS_DECISIONS_STATES,
  CYP_RISK_ASSESSMENT_STRUCTURES,
  CYP_BOA_CAPTURE_STATES,
  CYP_MDT_SUPPORTING_CLINICIAN_STATES,
  CYP_SYMPTOM_ONSET_CAPTURE_STATES,
  CYP_MSE_STRUCTURES,
  CYP_MEDICAL_SCREENING_STATES,
  ORG_WORKFLOW_CONFIGS,
} from '../constants/workflowConfig';

// ---------------------------------------------------------------------------
// deriveLegacyConfig — reproduces exact current hardcoded org-name behaviour
// ---------------------------------------------------------------------------

/**
 * Derives a workflow config object from an organisation name string,
 * reproducing the exact behaviour of the current hardcoded checks scattered
 * across the codebase.
 *
 * This is the ONLY place in the new system where org name strings appear.
 * Once all active patients have a stamped `workflowConfig`, this function
 * (and all its string checks) can be removed.
 *
 * IMPORTANT: The matching logic here deliberately mirrors the patterns used
 * in the original code — `includes()` for MDT/SLaM detection (substring
 * match), exact equality for CI exclusions, etc.  Changing the matching
 * logic would break legacy parity.
 *
 * @param {string} orgName - The organisation name from claims.organisation.
 * @returns {Object} A workflow config object matching DEFAULT_WORKFLOW_CONFIG shape.
 */
/**
 * SLaM / 3 Borough org-name match.
 *
 * Uses includes() substring matching — mirrors the original hardcoded checks
 * scattered across the codebase (DD, SSI conflict detection, patient summary,
 * etc.). Used by deriveLegacyConfig so SLaM org detection stays in one place.
 *
 * @param {string|null|undefined} orgName
 * @returns {boolean}
 */
export const isSlamOrganisationName = (orgName) =>
  !!orgName && (
    orgName.includes('SLaM') ||
    orgName.includes('3 Borough Adult ADHD and Autism Service')
  );

export const deriveLegacyConfig = (orgName) => {
  if (!orgName) return { ...DEFAULT_WORKFLOW_CONFIG };

  // ── SLaM / 3 Borough detection ──────────────────────────────────────────
  // Uses includes() to match substring — mirrors:
  //   1_DD.js:109       org.includes('SLaM') || org.includes('3 Borough...')
  //   ssiConflictDetection.js:33  SSI_FIRST_ORGANISATIONS.some(o => org.includes(o))
  //   buildOptimisticPatientSummary.js:28  organisation.includes('SLaM')
  //   patient_summary_functions.py:78     'SLaM' in organisation_name
  const isSLaM = isSlamOrganisationName(orgName);

  // ── MDT Review org detection ────────────────────────────────────────────
  // Uses includes() to match substring — mirrors:
  //   1_DD.js:108   org.includes('Well Life') || org.includes('MDT Org') || ...
  //   2_CSD.js:164  same pattern
  //   patientOverviewConfig.js: exact-match map keys for mdtReviewConfig
  const isMDT =
    orgName.includes('Well Life') ||
    orgName.includes('MDT Org') ||
    orgName.includes('ADHD and Autism Associates') ||
    orgName.includes('Lancaster Medical Practice') ||
    orgName.includes('The Owl Centre') ||
    orgName.includes('Demo Org');

  // ── CI section exclusions ───────────────────────────────────────────────
  // Uses exact equality — mirrors:
  //   clinicalInterviewWorkflow.js:20  organisationName === 'Psychological Consultations'
  const ciExclusions = [];
  if (orgName === 'Psychological Consultations') {
    ciExclusions.push('Mental Status Examination');
  }

  // ── PAD flow (Agreed Medication Plan on/off + stage order + structure) ──
  // Well Life Clinic, SLaM / 3 Borough, and Lancaster Medical Practice
  // record medication planning off-platform, so AMP is disabled and PAD ends
  // at Health Checks.  Everyone else keeps AMP enabled.  Reuses the `isSLaM`
  // variable above plus substring matches so legacy patients (no stamped
  // config) are covered.
  //
  // stageOrder: SLaM / 3 Borough run Treatment Options before Strategies in
  // the clinician PAD workflow; everyone else keeps Strategies-first.
  //
  // structure: LMP uses 'full' to get the SLaM-like DIS/TRT/HLT PAD instead
  // of TOC's "Next Steps" shape.  Absent structure falls through to the
  // resolution rule (tocAdhd => nextSteps, else => full).
  // ── TOC / Demo Org detection ──────────────────────────────────────────
  // Used for features shared between The Owl Centre and its Demo Org sandbox
  // (QbCheck passthrough, appointment dates, appointment checklist). Extracted
  // once so the substring checks cannot drift between features.
  const isTOCOrDemo =
    orgName.includes('The Owl Centre') || orgName.includes('Demo Org');

  const isLMP = orgName.includes('Lancaster Medical Practice');
  const padFlow = {
    agreedMedicationPlan: (orgName.includes('Well Life') || isSLaM || isLMP)
      ? PAD_MEDICATION_PLAN_STATES.DISABLED
      : PAD_MEDICATION_PLAN_STATES.ENABLED,
    stageOrder: isSLaM
      ? PAD_STAGE_ORDERS.TREATMENT_FIRST
      : PAD_STAGE_ORDERS.STRATEGIES_FIRST,
  };
  if (isLMP) {
    padFlow.structure = PAD_STRUCTURES.FULL;
  }

  // ── Supplementary documents ─────────────────────────────────────────────
  // Reproduces current org-name-gated visibility for legacy patients.
  // `patientData` is passed in when available (from resolveWorkflowConfig)
  // to read the patient-level workflowIncludesQbTest override for WLC.
  const supplementaryDocuments = { ...DEFAULT_SUPPLEMENTARY_DOCUMENTS };

  // Phoenix + Spectrum Mind: QbCheck with AI review pipeline
  if (
    orgName.includes('Phoenix Mental Health') ||
    orgName.includes('The Spectrum Mind')
  ) {
    supplementaryDocuments.qbCheck = { enabled: true, mode: QB_CHECK_MODES.AI_REVIEW };
  }
  // TOC / Demo Org: QbCheck in passthrough mode (manual score entry, auto-approved).
  // LMP is deliberately excluded — it mirrors TOC's workflow but does not run
  // QbCheck.
  if (isTOCOrDemo) {
    supplementaryDocuments.qbCheck = { enabled: true, mode: QB_CHECK_MODES.PASSTHROUGH };
  }
  // Phoenix: BUPA Letter
  if (orgName.includes('Phoenix Mental Health')) {
    supplementaryDocuments.bupaLetter = { enabled: true };
  }
  // A&A: Pre-Assessment Report
  if (orgName.includes('ADHD and Autism Associates')) {
    supplementaryDocuments.preAssessmentReport = { enabled: true };
  }
  // Well Life Clinic: Semble MDT Report
  if (orgName.includes('Well Life')) {
    supplementaryDocuments.sembleMdtReport = { enabled: true };
  }
  // MDT Org (internal testing): QbTest
  if (orgName.includes('MDT Org')) {
    supplementaryDocuments.qbTest = { enabled: true };
  }

  // ── Appointment dates ───────────────────────────────────────────────────
  // Per-module appointment date capture on Patient Overview (CI, SSI, DD, PAD).
  // Enabled for SLaM / 3 Borough and TOC / Demo Org.  Resolved with padFlow-
  // style fallback at runtime so already-stamped patients (whose workflowConfig
  // predates this field) still get the UI without a patient-level backfill.
  const appointmentDates = {
    enabled: isSLaM || isTOCOrDemo,
  };

  // ── Appointment Checklist (TOC / Demo Org) ─────────────────────────────
  // Start/end-of-appointment checklist on Adult Autism proformas. Enabled
  // only for The Owl Centre and Demo Org (reuses isTOCOrDemo detection).
  // Resolved with padFlow-style fallback at runtime so existing/in-flight
  // TOC patients (whose workflowConfig predates this field) still see the
  // checklist without a patient-level backfill.
  const appointmentChecklist = {
    enabled: isTOCOrDemo,
  };

  // ── Behavioural Observation on/off (obsFlow) ────────────────────────────
  // Lancaster Medical Practice runs a TOC-like Autism workflow but WITHOUT a
  // separate Behavioural Observation appointment (they do an MDT review in the
  // DD instead). We disable Observation for LMP here too so that any legacy /
  // unstamped LMP patient resolves the 4-stage pathway, matching the stamped
  // ORG_WORKFLOW_CONFIGS. Every other org omits obsFlow entirely → absent →
  // Observation enabled, so TOC and all existing autism patients are unchanged.
  const obsFlow = orgName.includes('Lancaster Medical Practice')
    ? { observation: OBS_OBSERVATION_STATES.DISABLED }
    : undefined;

  // ── Build the config ────────────────────────────────────────────────────
  // assessmentWorkflowType is always 'standard' in the legacy path.  Legacy
  // TOC patients remain on the standard workflow until explicitly stamped
  // with the TOC ADHD workflow via the admin stamp script.
  return {
    moduleOrder: isSLaM
      ? ['ssi', 'ci', 'dd', 'pad']
      : [...DEFAULT_WORKFLOW_CONFIG.moduleOrder],
    dfPreference: DEFAULT_WORKFLOW_CONFIG.dfPreference,
    ssiAccess: isSLaM
      ? SSI_ACCESS_POLICIES.ALWAYS
      : SSI_ACCESS_POLICIES.WHEN_ADHD,
    dfModel: DEFAULT_WORKFLOW_CONFIG.dfModel,
    ddStyle: isMDT ? DD_STYLES.MDT_REVIEW : DD_STYLES.STANDARD,
    assessmentWorkflowType: ASSESSMENT_WORKFLOW_TYPES.STANDARD,
    // Legacy path never opts into Report Builder — Phase 2 SLaM only via stamp.
    replaceCARwithReportBuilder: false,
    ciExclusions,
    padFlow,
    supplementaryDocuments,
    appointmentDates,
    appointmentChecklist,
    // Only present (and disabled) for LMP; omitted elsewhere so the resolver's
    // "absent → enabled" default keeps Observation on for every other org.
    ...(obsFlow ? { obsFlow } : {}),
  };
};

// ---------------------------------------------------------------------------
// resolveWorkflowConfig — pure function for config + computed helpers
// ---------------------------------------------------------------------------

/**
 * Core resolution logic extracted as a pure function so it can be
 * exhaustively unit-tested without React.  The hook is a thin wrapper
 * that memoises this result.
 *
 * @param {Object|null} patientDataModel - The patient's Firestore document.
 * @param {Object|null} claims - The authenticated user's Firebase claims.
 * @returns {Object} Resolved workflow config with computed helper booleans.
 */
export const resolveWorkflowConfig = (patientDataModel, claims) => {
  // 1. Try patient-stamped config (new patients)
  const stamped = patientDataModel?.workflowConfig;

  // 2. Fall back to the hardcoded preset for the org (if one exists), then
  //    to the legacy org-name derivation (existing patients without a stamp).
  //
  //    The preset lookup is used ONLY when no patient model is provided at
  //    all (patientDataModel is null/undefined). This covers callers like
  //    the dashboard progress pills that pass only an org name and null
  //    patient — without it, orgs whose names do not match the legacy
  //    substring checks (e.g. Urban ADHD) would resolve to the wrong config.
  //
  //    When a patient model IS provided but has no workflowConfig stamp,
  //    that is a legacy patient who was created before the stamping system.
  //    Legacy patients must go through deriveLegacyConfig so they get the
  //    conservative legacy behaviour (no Report Builder, no appointment
  //    dates, etc.) rather than the full modern preset.
  const orgName = claims?.organisation;
  const usePresetFallback = !patientDataModel && orgName && ORG_WORKFLOW_CONFIGS[orgName];
  const config = stamped
    || (usePresetFallback ? { ...ORG_WORKFLOW_CONFIGS[orgName] } : deriveLegacyConfig(orgName));

  // 3. Read mutable workflow decisions (e.g. dfDeferred)
  const decisions = patientDataModel?.workflowDecisions || {};

  // 4. Compute helpers — these save every consumer from having to compare
  //    enum strings.  Named to match the boolean questions developers ask:
  //    "is SSI first?", "is this an MDT review org?", etc.
  const isSSIFirst = config.moduleOrder?.[0] === 'ssi';
  const isSSIAlwaysExpected = config.ssiAccess === SSI_ACCESS_POLICIES.ALWAYS;
  const isFlatDifferentials = config.dfModel === DF_MODELS.FLAT;
  const isMDTReview = config.ddStyle === DD_STYLES.MDT_REVIEW;
  const dfPreferredEndOfSSI = config.dfPreference === DF_PREFERENCES.END_OF_SSI;

  // Assessment workflow type: determines the overall platform behaviour,
  // PAD flow structure, and report rendering pipeline for this patient.
  // Defaults to 'standard' when absent (existing stamped configs pre-dating
  // this field, or legacy patients falling through deriveLegacyConfig).
  const assessmentWorkflowType = config.assessmentWorkflowType || ASSESSMENT_WORKFLOW_TYPES.STANDARD;
  const isTOCWorkflow = assessmentWorkflowType === ASSESSMENT_WORKFLOW_TYPES.TOC_ADHD;
  const isSLaMWorkflow = assessmentWorkflowType === ASSESSMENT_WORKFLOW_TYPES.SLAM_ADHD;
  const isWLCWorkflow = assessmentWorkflowType === ASSESSMENT_WORKFLOW_TYPES.WLC_ADHD;
  // TOC, SLaM, and WLC all render section CARs via pl_* placeholders + the
  // {{IF:}} conditional engine (including ADHD_POSITIVE / domain-impairment
  // framing on the SSI CAR). Standard orgs keep the legacy heading-removal
  // CAR path. Mirrors backend patient_uses_conditional_car_pipeline().
  const usesConditionalCarPipeline = isTOCWorkflow || isSLaMWorkflow || isWLCWorkflow;

  // replaceCARwithReportBuilder — progressive "Report Builder" on Manage Documents
  // (no standalone Cover Page row / generation). Resolution is stamp-only so
  // SLaM Phase 1 pilot patients keep Clinical Assessment Report + Cover Page:
  //   1. stamped tocAdhd → omit (existing TOC behaviour)
  //   2. patient stamp replaceCARwithReportBuilder === true → omit (SLaM Phase 2
  //      after org assessmentWorkflow is updated; new patients only)
  //   3. otherwise keep Cover Page (Phase 1 slamAdhd stamps, legacy, standard)
  //
  // Do NOT gate on isSLaMWorkflow or org name — Phase 1 patients already have
  // slamAdhd stamped. Download visibility stays on !isTOCWorkflow so Phase 2
  // SLaM keeps "Download Sections" while TOC hides the bundle button.
  const replaceCARwithReportBuilder =
    isTOCWorkflow ||
    config.replaceCARwithReportBuilder === true;

  // DF deferral: if the clinician has explicitly chosen (dfDeferred is
  // true or false), respect their decision.  If no choice has been made
  // yet (undefined), fall back to the org's dfPreference — orgs that
  // prefer DF at end of SSI default to deferred until the clinician
  // opts to do it during CI.
  const isDFDeferred = decisions.dfDeferred !== undefined
    ? decisions.dfDeferred === true
    : dfPreferredEndOfSSI;

  // Appointment finish path (SLaM / 3 Borough): set when the Lead finishes
  // the last interview module of the appointment — CI Review & Synthesis for
  // two-clinician patients, SSI Review & Synthesis for one-clinician patients
  // (see appointmentFinishPathModule below). Absent until then — overview
  // defaults to PAD before DD. The key keeps its historical name so every
  // reader works for both generations of patient.
  // 'padInAppointment' | 'deferPad' | null
  const ciFinishPath = decisions.ciFinishPath || null;

  // End-of-CI decision (one-clinician SLaM / 3 Borough): set when the Lead
  // finishes the Clinical Interview and chooses to proceed to the SSI or to
  // end the assessment. Absent until then (and cleared again by "Resume
  // assessment"). 'proceedToSsi' | 'endAssessment' | null
  const ciEndDecision = decisions.ciEndDecision || null;

  // Whether the DD should always show the ADHD diagnostic decision flow.
  // True when the SSI module has been started (has any data), because
  // the SSI is an ADHD-specific deep-dive and its findings must be
  // formally addressed in the DD regardless of what the DF concluded.
  const ssiStage = patientDataModel?.dataModel?.['3_semiStructuredInterview'];
  const ssiHasData = !!(ssiStage && (ssiStage.status || ssiStage.sections));
  const shouldShowAdhdDDFlow = ssiHasData;

  // ── Module Flow Objects ─────────────────────────────────────────────────
  // Nested per-module config with backward-compat fallbacks for patients
  // stamped before these fields existed (e.g. existing TOC patients).
  const ssiFlow = config.ssiFlow || {};
  const ciFlow = config.ciFlow || {};
  const ddFlow = config.ddFlow || {};

  // padFlow uses the same three-tier resolution as supplementaryDocuments so
  // the setting applies to EVERY patient of a gated org without a data
  // migration: (1) the patient's own stamped padFlow, else (2) the org-name
  // legacy derivation (covers patients stamped before padFlow existed AND
  // fully-legacy patients), else (3) an empty object (enabled default).
  //
  // stageOrder is resolved PER-KEY from legacy when the stamped padFlow
  // omits it. That covers SLaM patients stamped when padFlow only carried
  // agreedMedicationPlan — they still get treatment-first without a
  // patient-level backfill. Do not rely on isSLaMWorkflow for this: legacy
  // SLaM patients have assessmentWorkflowType 'standard'.
  const legacyPadFlow =
    deriveLegacyConfig(claims?.organisation).padFlow || {};
  const padFlow = config.padFlow || legacyPadFlow || {};
  const padStageOrder =
    padFlow.stageOrder ||
    legacyPadFlow.stageOrder ||
    PAD_STAGE_ORDERS.STRATEGIES_FIRST;

  // SSI end page: which page concludes the SSI module.
  // Fallback: isDFDeferred implies Review & Synthesis (TOC backward compat).
  const ssiEndPage = ssiFlow.endPage || (isDFDeferred ? SSI_END_PAGES.REVIEW_AND_SYNTHESIS : SSI_END_PAGES.RDD);

  // CI end page: which page concludes the CI module (section 12).
  // Fallback: standard Diagnostic Formulation unless explicitly overridden.
  const ciEndPage = ciFlow.endPage || CI_END_PAGES.DIAGNOSTIC_FORMULATION;

  // DD structure: how many pages and what navigation the DD module uses.
  // Fallback: isTOCWorkflow implies multiStep (TOC backward compat).
  const ddStructure = ddFlow.structure || (isTOCWorkflow ? DD_STRUCTURES.MULTI_STEP : DD_STRUCTURES.STANDARD);

  // Derived booleans — answer the product questions each page asks.
  // showSSIReviewPage: SSI concludes with Review & Synthesis (not RDD).
  const showSSIReviewPage = ssiEndPage === SSI_END_PAGES.REVIEW_AND_SYNTHESIS;
  // hasCIReview: CI section 12 renders as Review & Synthesis (not standard DF).
  const hasCIReview = ciEndPage === CI_END_PAGES.REVIEW_AND_SYNTHESIS;
  // isCIRiskAssessmentEnabled: whether the Risk Assessment stage is part of the
  // CI ("History" in the Adult Autism app). Absent -> enabled, so legacy
  // patients (no ciFlow.riskAssessment stamp) keep Risk Assessment untouched;
  // only patients explicitly stamped 'disabled' lose it. Mirrors the backend
  // resolver (is_ci_risk_assessment_enabled in workflow_config_utils.py).
  const isCIRiskAssessmentEnabled =
    ciFlow.riskAssessment !== CI_RISK_ASSESSMENT_STATES.DISABLED;
  // isObservationEnabled: whether the Behavioural Observation stage is part of
  // the Adult Autism assessment. Absent -> enabled, so TOC and every existing
  // autism patient (no obsFlow.observation stamp) keep Observation; only
  // patients explicitly stamped 'disabled' (e.g. LMP autism) drop it. Mirrors
  // the backend resolver (is_observation_enabled in workflow_config_utils.py).
  const obsFlow = config.obsFlow || {};
  const isObservationEnabled =
    obsFlow.observation !== OBS_OBSERVATION_STATES.DISABLED;
  // isObsV2: whether this patient uses the redesigned Behavioural Observation
  // (tagged notes + criteria-level evidence + Other Behaviours paragraph).
  // Self-contained cutover flag read from the stamped config ONLY — there is no
  // legacy org-name derivation, so absent obsFlow.version resolves to v1 and
  // every existing / in-flight autism patient keeps the original task-by-task
  // Criterion Mapping and v1 LLM pipeline. Mirrors is_obs_v2 in the backend
  // workflow_config_utils.py.
  const isObsV2 = obsFlow.version === OBS_VERSIONS.V2;
  // hasMultiStepDD: DD uses the 4-page wizard with sidebar navigation.
  const hasMultiStepDD = ddStructure === DD_STRUCTURES.MULTI_STEP;

  // skipDifferentialsDecisions: whether the ADHD DD module skips the "Decision
  // on Differentials" / "Decision for Other Conditions" step. When true,
  // emerging differentials are shown as a read-only list on the Final
  // Formulation page but clinicians do not make per-condition decisions.
  // Runtime fallback: TOC patients created before the explicit ddFlow stamp
  // was backfilled get the new behaviour automatically (isTOCWorkflow is
  // derived from assessmentWorkflowType which is already on their doc). Orgs
  // with an explicit 'enabled' stamp keep the decisions step even if they
  // happen to use a TOC workflow type (e.g. LMP).
  const skipDifferentialsDecisions =
    ddFlow.differentialsDecisions === DD_DIFFERENTIALS_DECISIONS_STATES.DISABLED
    || (!ddFlow.differentialsDecisions && isTOCWorkflow);

  // skipAutismDifferentialsDecisions: same behaviour but for the Adult Autism
  // DD pathway. Reads from the separate autismDdFlow namespace so each pathway
  // can be independently toggled per org (follows the cypAdhdFlow precedent).
  // Runtime fallback mirrors the ADHD flag: isTOCWorkflow patients without an
  // explicit autismDdFlow stamp get the new behaviour automatically.
  const autismDdFlow = config.autismDdFlow || {};
  const skipAutismDifferentialsDecisions =
    autismDdFlow.differentialsDecisions === DD_DIFFERENTIALS_DECISIONS_STATES.DISABLED
    || (!autismDdFlow.differentialsDecisions && isTOCWorkflow);

  // ddHidePartiallyMet: whether the adult ADHD Decision for ADHD omits the
  // "Partially met" answer. Stamp-only and compared to exactly true, with NO
  // workflow-type or org-name fallback (unlike skipDifferentialsDecisions
  // above): the option must stay visible for every patient stamped before
  // this key existed, since some of them have already recorded it. Nothing
  // reads the org preset once a patient document exists, so an org backfill
  // reaches new patients only. No adult org sets this yet.
  const ddHidePartiallyMet = ddFlow.hidePartiallyMet === true;

  // allowLeadSSIOverride: Lead may take over SSI editing/review after the
  // SSI is completed (SLaM / 3 Borough). Stamped-config only — legacy patients
  // without ssiFlow.leadOverride keep the older conflict/reset flow untouched.
  // Gated on stamped config (not org-name legacy) so autism patients that
  // share a SLaM org stamp still need the ADHD SSI stage present at the CF.
  const allowLeadSSIOverride =
    ssiFlow.leadOverride === SSI_LEAD_OVERRIDE_STATES.ENABLED;

  // appointmentFinishPathModule: which module (if any) ends the appointment
  // with the "share the outcome now / end the appointment" wizard that writes
  // workflowDecisions.ciFinishPath.
  //   'ci'  — two-clinician SLaM: the CI ends with Review & Synthesis and the
  //           SSI was done by someone else earlier, so the CI is the last
  //           interview of the appointment (existing behaviour, keyed off
  //           hasCIReview so every stamped two-clinician patient is unchanged).
  //   'ssi' — one-clinician SLaM: the Lead runs CI then SSI, so the SSI
  //           Review & Synthesis is the last interview of the appointment.
  //           Stamp-only (ssiFlow.appointmentFinishPath === 'enabled').
  //   null  — every other org: no wizard, the modules finish as today.
  // hasCIReview wins if an org were ever stamped with both, so the question is
  // only asked once per appointment. Mirrors appointment_finish_path_module
  // in workflow_config_utils.py.
  const appointmentFinishPathModule = hasCIReview
    ? 'ci'
    : ssiFlow.appointmentFinishPath === SSI_APPOINTMENT_FINISH_PATH_STATES.ENABLED
      ? 'ssi'
      : null;

  // allowEndAssessmentAfterCI: whether finishing the Clinical Interview asks
  // the clinician to proceed to the SSI or end the assessment with a reason
  // (one-clinician SLaM / 3 Borough). Stamp-only, absent -> false, so every
  // other org and every legacy patient keeps the plain CI completion dialog.
  // Mirrors allow_end_assessment_after_ci in workflow_config_utils.py.
  const allowEndAssessmentAfterCI =
    ciFlow.endAssessmentDecision === CI_END_ASSESSMENT_DECISION_STATES.ENABLED;

  // assessmentEndedAfterCI: the clinician recorded "end assessment" at the
  // end of the CI and nobody has resumed it since. Drives the locked SSI/DD/PAD
  // cards, the Assessment ended banner, and the Assessment Conclusion row in
  // the CI report review. Read from the decision alone (not gated on the
  // stamp) so a patient whose org preset later changed still shows its true
  // historical state. Mirrors assessment_ended_after_ci in the backend.
  const assessmentEndedAfterCI =
    ciEndDecision === CI_END_DECISIONS.END_ASSESSMENT;

  // isSSIDomainsImpactedEnabled: whether the "Domains Impacted" chip rows are
  // shown on each criterion panel and the domain impact summary table on the
  // SSI Review page. Absent -> enabled, so every legacy patient and every org
  // that has not opted out keeps domains visible. Only patients explicitly
  // stamped 'disabled' (e.g. new TOC patients after org backfill) lose them.
  // Mirrors is_ssi_domains_impacted_enabled in workflow_config_utils.py.
  const isSSIDomainsImpactedEnabled =
    ssiFlow.domainsImpacted !== SSI_DOMAINS_IMPACTED_STATES.DISABLED;

  // requireEmergingDifferentials: whether the Emerging Differentials panel
  // on SSI Review & Synthesis gates the Complete button.
  //
  // Three-tier resolution:
  //   1. Explicit 'optional' stamp  -> false (WLC V2 / TOC new patients)
  //   2. Explicit 'required' stamp  -> true  (future opt-in)
  //   3. Absent key -> conditional default:
  //        wlcAdhd patients  -> false (in-flight WLC V2 test patients
  //        whose immutable stamp predates this field)
  //        tocAdhd patients  -> false (existing TOC patients stamped
  //        before emergingDifferentials was added to the org config)
  //        everyone else     -> true  (SLaM, standard — preserves
  //        existing mandatory behaviour)
  //
  // Follows the same conditional-absent-stamp pattern used by ddStructure
  // (absent + isTOCWorkflow -> multiStep) and padStructure (absent +
  // isTOCWorkflow -> nextSteps).
  const requireEmergingDifferentials =
    ssiFlow.emergingDifferentials === SSI_EMERGING_DIFFERENTIALS_STATES.OPTIONAL
      ? false
      : ssiFlow.emergingDifferentials === SSI_EMERGING_DIFFERENTIALS_STATES.REQUIRED
        ? true
        : !(isWLCWorkflow || isTOCWorkflow);

  // isAgreedMedicationPlanEnabled: whether the PAD includes the Agreed
  // Medication Plan (AMP) stage.  When false (Well Life Clinic, SLaM /
  // 3 Borough) the AMP stage is hidden from the PAD sidebar, the "Yes"
  // option is removed from "Create medication plan now?", and the AMP route
  // redirects — so the PAD ends at Health Checks.  Defaults to enabled when
  // unspecified.
  const isAgreedMedicationPlanEnabled =
    padFlow.agreedMedicationPlan !== PAD_MEDICATION_PLAN_STATES.DISABLED;

  // isPadTreatmentFirst: whether the clinician PAD workflow shows Treatment
  // Options before Strategies & Recommendations (SLaM / 3 Borough). When
  // true, display numbers renumber (Treatment = 2.x, Strategies = 3.x),
  // prev/next links swap, and the "Carry out health checks now?" gate UI
  // is shown at the end of Strategies (still writing the Treatment Options
  // Firestore path). Defaults to false (strategies-first) when unspecified.
  const isPadTreatmentFirst =
    padStageOrder === PAD_STAGE_ORDERS.TREATMENT_FIRST;

  // PAD structure — which shape the Post-Assessment Discussion takes.
  // Resolution rule mirrors ddFlow.structure exactly: explicit stamp wins;
  // absent stamp falls through to workflow-type default. LMP (tocAdhd +
  // padFlow.structure: full) gets the SLaM-like DIS/TRT/HLT PAD; TOC
  // (tocAdhd, no padFlow.structure) keeps Next Steps.
  const padStructure =
    padFlow.structure ||
    legacyPadFlow.structure ||
    (isTOCWorkflow ? PAD_STRUCTURES.NEXT_STEPS : PAD_STRUCTURES.FULL);
  const usesNextStepsPad = padStructure === PAD_STRUCTURES.NEXT_STEPS;

  // isAdhdControlledDrugPrescribingEnabled: whether the Health Checks stage
  // includes the ADHD / Controlled Drug Prescribing panel. Read exclusively
  // from the stamped config (no legacy fallback) so the feature only appears
  // on patients created after the org-doc backfill (FR9 requirement).
  const isAdhdControlledDrugPrescribingEnabled =
    stamped?.padFlow?.adhdControlledDrugPrescribing === 'enabled';

  // ── CYP ADHD Flow ───────────────────────────────────────────────────────
  // CYP ADHD pathway config (assessmentModule 'cypAdhd'). CYP pages read ONLY
  // these cyp* helpers. Resolution is CYP-explicit end to end: values come
  // from config.cypAdhdFlow alone, and absent-key defaults are fixed CYP
  // defaults — never the adult ssiFlow/ddFlow keys, never conditionals on
  // assessmentWorkflowType (CYP patients share the org's assessmentWorkflow
  // stamp with the adult pathway, so adult keys ARE present in config; they
  // must never leak into CYP behaviour).
  const cypAdhdFlow = config.cypAdhdFlow || {};
  // SSI end page: Review & Synthesis is the only CYP ending; absent → same.
  const cypSsiEndPage =
    cypAdhdFlow.ssiEndPage || CYP_SSI_END_PAGES.REVIEW_AND_SYNTHESIS;
  const cypShowSSIReviewPage =
    cypSsiEndPage === CYP_SSI_END_PAGES.REVIEW_AND_SYNTHESIS;
  // Emerging differentials gate: only an explicit 'required' stamp gates the
  // CYP SSI Review & Synthesis Complete button. Absent or 'optional' → false
  // (Well Life Clinic decision). Deliberately independent of the adult
  // requireEmergingDifferentials resolution above.
  const cypRequireEmergingDifferentials =
    cypAdhdFlow.emergingDifferentials ===
    CYP_SSI_EMERGING_DIFFERENTIALS_STATES.REQUIRED;
  // DD structure: multi-step wizard ending at Final Formulation (no MDT Plan
  // step) is the only CYP structure; absent → same.
  const cypDdStructure =
    cypAdhdFlow.ddStructure || CYP_DD_STRUCTURES.FINAL_FORMULATION;
  const cypDdEndsAtFinalFormulation =
    cypDdStructure === CYP_DD_STRUCTURES.FINAL_FORMULATION;
  // cypHasMultiStepDD: CYP DD pages read this instead of the adult
  // hasMultiStepDD so that any CYP patient — regardless of whether the
  // immutable workflowConfig stamp includes the adult ddFlow.structure key —
  // unconditionally gets the 4-stage wizard. There is no single-page CYP DD,
  // so this is always true today; the flag exists so the CYP pathway never
  // accidentally reads the adult ddFlow stamp again.
  const cypHasMultiStepDD =
    cypDdStructure === CYP_DD_STRUCTURES.FINAL_FORMULATION;
  // Decision on Differentials step in the CYP DD: only an explicit DISABLED
  // stamp skips it (TOC, Oct 2026 — parity with TOC's adult ADHD / Autism
  // DDs). Absent or ENABLED → the step is asked (WLC V2, and every patient
  // stamped before this key). Deliberately independent of the adult
  // skipDifferentialsDecisions above, including its TOC runtime fallback:
  // CYP never infers behaviour from the workflow type.
  const cypDdDifferentialsDecisions =
    cypAdhdFlow.differentialsDecisions
    || CYP_DD_DIFFERENTIALS_DECISIONS_STATES.ENABLED;
  const cypSkipDifferentialsDecisions =
    cypDdDifferentialsDecisions === CYP_DD_DIFFERENTIALS_DECISIONS_STATES.DISABLED;
  // "Partially met" on the CYP Decision for ADHD: hidden only by an explicit
  // true on the CYP stamp (WLC and TOC, Oct 2026). Absent or false shows it,
  // so a child stamped before this key keeps an answer they may already have
  // recorded. Reads the CYP key only — never the adult ddHidePartiallyMet.
  const cypDdHidePartiallyMet = cypAdhdFlow.hidePartiallyMet === true;
  // Risk Assessment shape in the CYP CI: only an explicit structuredV1 stamp
  // selects TOC's NEST-parity structured assessment. Absent or 'standard' →
  // the original panel (WLC, and every patient stamped before this key).
  // The CYP Risk Assessment is always ON — this never disables it, and it is
  // deliberately independent of the adult ciFlow.riskAssessment switch.
  const cypRiskAssessmentStructure =
    cypAdhdFlow.riskAssessment || CYP_RISK_ASSESSMENT_STRUCTURES.STANDARD;
  const cypUseStructuredRiskAssessment =
    cypRiskAssessmentStructure === CYP_RISK_ASSESSMENT_STRUCTURES.STRUCTURED_V1;
  // BOA capture: only an explicit mdtReviewTab stamp shows the Behavioural
  // Observation Appointment step at the start of the CYP Diagnostic Decision
  // (TOC, Sept 2026 — capture only, no report wiring yet). Absent → disabled
  // (WLC, and every patient stamped before this key).
  const cypBoaCapture =
    cypAdhdFlow.boaCapture || CYP_BOA_CAPTURE_STATES.DISABLED;
  const cypShowBoaMdtTab =
    cypBoaCapture === CYP_BOA_CAPTURE_STATES.MDT_REVIEW_TAB;
  // Supporting clinician at the CYP MDT Review: an explicit enabled stamp
  // shows the add-MDT-clinician action on the Diagnostic Decision card of
  // the CYP patient overview (TDI-208 — the MDT decision is made jointly).
  // Both WLC and TOC now stamp ENABLED; absent → disabled (patients stamped
  // before this key existed).
  const cypMdtSupportingClinician =
    cypAdhdFlow.mdtSupportingClinician ||
    CYP_MDT_SUPPORTING_CLINICIAN_STATES.DISABLED;
  const cypEnableMdtSupportingClinician =
    cypMdtSupportingClinician === CYP_MDT_SUPPORTING_CLINICIAN_STATES.ENABLED;
  // Symptom onset capture in the CYP SSI: both WLC and TOC now stamp
  // THIRTEEN_PLUS_BEFORE_ONLY — "present before age 12" asked only when
  // the child is 13 or older (onset is implied for younger children).
  // Absent → the same 13-plus before-only behaviour (patients stamped
  // before this key). The pair mode remains valid for any future org that
  // needs it.
  const cypSymptomOnsetCapture =
    cypAdhdFlow.symptomOnsetCapture ||
    CYP_SYMPTOM_ONSET_CAPTURE_STATES.THIRTEEN_PLUS_BEFORE_ONLY;
  const cypUseOnsetPair =
    cypSymptomOnsetCapture ===
    CYP_SYMPTOM_ONSET_CAPTURE_STATES.ALL_AGES_BEFORE_AFTER_PAIR;
  // cypAdhdFlow.mse. Absent → the adult mirror.
  //   noObservationInSsi (WLC and TOC) → the CI keeps its MSE, using the
  //     child-specific Clinical Observation questions. The SSI has no
  //     observation phase.
  //   observationInSsi → the other variant. The CI has no MSE step; the SSI
  //     opens with the Clinical Observation phase. No org stamps this.
  // cypUseClinicalObservationMse asks "which MSE questionnaire?" and is true
  // for noObservationInSsi. cypHasObservationInSsi and cypHideCiMse ask
  // "is the SSI-observation variant on?" A reader of either page should not
  // have to know about the other.
  const cypMseStructure =
    cypAdhdFlow.mse || CYP_MSE_STRUCTURES.ADULT_MIRROR;
  const cypUseClinicalObservationMse =
    cypMseStructure === CYP_MSE_STRUCTURES.NO_OBSERVATION_IN_SSI;
  const cypHasObservationInSsi =
    cypMseStructure === CYP_MSE_STRUCTURES.OBSERVATION_IN_SSI;
  const cypHideCiMse = cypHasObservationInSsi;

  // cypAdhdFlow.medicalScreening. Absent → disabled, so a patient stamped
  // before this key existed (or an org that has not opted in) sees no
  // change. Only contraindicationsV1 turns the panel on; the pages gate on
  // the boolean so they never need to know which version is live. If a V2
  // item list arrives, add a state here and the config module picks the
  // list by state -- the pages keep reading cypShowMedicalScreening.
  const cypMedicalScreening =
    cypAdhdFlow.medicalScreening || CYP_MEDICAL_SCREENING_STATES.DISABLED;
  const cypShowMedicalScreening =
    cypMedicalScreening === CYP_MEDICAL_SCREENING_STATES.CONTRAINDICATIONS_V1;

  // ── Supplementary documents ────────────────────────────────────────────
  // Three-tier resolution:
  // 1. Stamped supplementaryDocuments (new patients after this migration)
  // 2. Legacy derivation from org name (Phase 1 patients whose stamped
  //    config pre-dates supplementaryDocuments, OR fully legacy patients)
  // 3. DEFAULT_SUPPLEMENTARY_DOCUMENTS (safety net if org is unknown)
  let suppDocs;
  if (stamped?.supplementaryDocuments) {
    suppDocs = stamped.supplementaryDocuments;
  } else {
    const legacySupp = deriveLegacyConfig(claims?.organisation).supplementaryDocuments;
    suppDocs = legacySupp || DEFAULT_SUPPLEMENTARY_DOCUMENTS;
  }

  // WLC patient-level override: historical WLC patients have a boolean
  // workflowIncludesQbTest field that overrides the org-level config.
  // Only relevant for legacy WLC patients where QbTest was once enabled
  // org-wide but later disabled for new patients.
  const patientLevelQbTestOverride = patientDataModel?.workflowIncludesQbTest;
  const qbTestConfig = patientLevelQbTestOverride === true
    ? { enabled: true }
    : (suppDocs.qbTest || { enabled: false });

  const qbCheckConfig = suppDocs.qbCheck || { enabled: false };
  const isQbCheckEnabled = qbCheckConfig.enabled === true;
  const qbCheckMode = isQbCheckEnabled ? (qbCheckConfig.mode || null) : null;
  const isQbTestEnabled = qbTestConfig.enabled === true;
  const isBupaLetterEnabled = (suppDocs.bupaLetter || { enabled: false }).enabled === true;
  const isPreAssessmentReportEnabled = (suppDocs.preAssessmentReport || { enabled: false }).enabled === true;
  // Semble MDT Report: gated at the patient level via the stamped config.
  // Only patients whose workflowConfig was stamped (at creation or via admin
  // backfill) with sembleMdtReport.enabled = true get the feature.  This
  // ensures the MDT Plan / Semble Report is only available to patients who
  // had not yet started their assessment when the feature was enabled —
  // the backfill selectively stamps eligible patients, and setupNewPatient
  // automatically includes it from the org's assessmentWorkflow for new ones.
  const isSembleMdtReportEnabled =
    stamped?.supplementaryDocuments?.sembleMdtReport?.enabled === true;

  // Appointment dates: padFlow-style three-tier resolution so the setting
  // applies to EVERY patient of a gated org without a data migration:
  //   (1) the patient's own stamped appointmentDates, else
  //   (2) the org-name legacy derivation (covers patients stamped before
  //       appointmentDates existed AND fully-legacy patients), else
  //   (3) DEFAULT_APPOINTMENT_DATES (enabled: false).
  // Do not rely on isSLaMWorkflow for this: legacy SLaM patients have
  // assessmentWorkflowType 'standard'.
  const legacyAppointmentDates =
    deriveLegacyConfig(claims?.organisation).appointmentDates || DEFAULT_APPOINTMENT_DATES;
  const appointmentDatesCfg =
    config.appointmentDates || legacyAppointmentDates || DEFAULT_APPOINTMENT_DATES;
  const isAppointmentDatesEnabled = appointmentDatesCfg.enabled === true;

  // Appointment Checklist: padFlow-style three-tier resolution so the setting
  // applies to EVERY patient of a gated org (TOC, Demo Org) without a
  // patient-level data migration:
  //   (1) the patient's own stamped appointmentChecklist (new patients after
  //       org backfill), else
  //   (2) the org-name legacy derivation (existing TOC/Demo patients whose
  //       workflowConfig predates this field, or fully legacy patients), else
  //   (3) { enabled: false } (safe default — unknown orgs never show it).
  const DEFAULT_APPOINTMENT_CHECKLIST = { enabled: false };
  const legacyAppointmentChecklist =
    deriveLegacyConfig(claims?.organisation).appointmentChecklist || DEFAULT_APPOINTMENT_CHECKLIST;
  const appointmentChecklistCfg =
    config.appointmentChecklist || legacyAppointmentChecklist || DEFAULT_APPOINTMENT_CHECKLIST;
  const isAppointmentChecklistEnabled = appointmentChecklistCfg.enabled === true;

  return {
    // Raw config fields
    moduleOrder: config.moduleOrder,
    dfPreference: config.dfPreference,
    ssiAccess: config.ssiAccess,
    dfModel: config.dfModel,
    ddStyle: config.ddStyle,
    assessmentWorkflowType,
    ciExclusions: config.ciExclusions || [],
    assessmentFormProfile: config.assessmentFormProfile || null,
    supplementaryDocuments: suppDocs,
    appointmentDates: appointmentDatesCfg,

    // Raw module flow objects (nested config)
    ssiFlow,
    ciFlow,
    obsFlow,
    ddFlow,
    autismDdFlow,
    padFlow,

    // Computed helpers — core modules
    isSSIFirst,
    isSSIAlwaysExpected,
    isFlatDifferentials,
    isMDTReview,
    dfPreferredEndOfSSI,
    isDFDeferred,
    ciFinishPath,
    ciEndDecision,
    shouldShowAdhdDDFlow,

    // Computed helpers — assessment workflow type
    isTOCWorkflow,
    isSLaMWorkflow,
    isWLCWorkflow,
    usesConditionalCarPipeline,
    replaceCARwithReportBuilder,

    // Computed helpers — module flow (derived from nested flow objects)
    showSSIReviewPage,
    hasCIReview,
    isCIRiskAssessmentEnabled,
    isObservationEnabled,
    isObsV2,
    hasMultiStepDD,
    skipDifferentialsDecisions,
    skipAutismDifferentialsDecisions,
    ddHidePartiallyMet,
    allowLeadSSIOverride,
    appointmentFinishPathModule,
    allowEndAssessmentAfterCI,
    assessmentEndedAfterCI,
    isSSIDomainsImpactedEnabled,
    requireEmergingDifferentials,
    isAgreedMedicationPlanEnabled,
    isPadTreatmentFirst,
    padStructure,
    usesNextStepsPad,
    isAdhdControlledDrugPrescribingEnabled,

    // Computed helpers — CYP ADHD pathway (read only these on CYP pages)
    cypAdhdFlow,
    cypSsiEndPage,
    cypShowSSIReviewPage,
    cypRequireEmergingDifferentials,
    cypDdStructure,
    cypDdEndsAtFinalFormulation,
    cypHasMultiStepDD,
    cypDdDifferentialsDecisions,
    cypSkipDifferentialsDecisions,
    cypDdHidePartiallyMet,
    cypRiskAssessmentStructure,
    cypUseStructuredRiskAssessment,
    cypBoaCapture,
    cypShowBoaMdtTab,
    cypMdtSupportingClinician,
    cypEnableMdtSupportingClinician,
    cypSymptomOnsetCapture,
    cypUseOnsetPair,
    cypMseStructure,
    cypUseClinicalObservationMse,
    cypHasObservationInSsi,
    cypHideCiMse,
    cypMedicalScreening,
    cypShowMedicalScreening,

    // Computed helpers — supplementary documents
    isQbCheckEnabled,
    qbCheckMode,
    isQbTestEnabled,
    isBupaLetterEnabled,
    isPreAssessmentReportEnabled,
    isSembleMdtReportEnabled,

    // Computed helpers — appointment dates
    isAppointmentDatesEnabled,

    // Computed helpers — appointment checklist (Adult Autism proformas)
    isAppointmentChecklistEnabled,

    // Meta
    isLegacyPatient: !stamped,
  };
};

// ---------------------------------------------------------------------------
// useWorkflowConfig — React hook
// ---------------------------------------------------------------------------

/**
 * React hook that resolves the assessment workflow config for the current
 * patient.  Memoised on the relevant input fields to avoid unnecessary
 * recomputation on every render.
 *
 * @param {Object|null} patientDataModel - The patient's Firestore document.
 * @param {Object|null} claims - The authenticated user's Firebase claims.
 * @returns {Object} Resolved workflow config with computed helper booleans.
 */
const useWorkflowConfig = (patientDataModel, claims) => {
  const stampedConfig = patientDataModel?.workflowConfig;
  const workflowDecisions = patientDataModel?.workflowDecisions;
  const ssiStage = patientDataModel?.dataModel?.['3_semiStructuredInterview'];
  const organisation = claims?.organisation;

  return useMemo(
    () => resolveWorkflowConfig(patientDataModel, claims),
    // Only the fields that resolveWorkflowConfig actually reads need to
    // trigger recomputation.  The four extracted variables cover the full
    // set of inputs; the eslint rule is satisfied because patientDataModel
    // and claims are closed over but correctly represented by their
    // relevant sub-properties here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stampedConfig, workflowDecisions, ssiStage, organisation]
  );
};

export default useWorkflowConfig;
